import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Plus, Loader2 } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useToast } from '@/hooks/use-toast';
import { sprintService } from '@/services/sprint.service';
import { groupService } from '@/services/group.service';

interface Evaluator {
  id: string;
  user: { id: string; full_name: string; avatar_url: string | null };
}

interface SprintEvaluatorsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sprintId: string;
  groupId: string;
  evaluators: Evaluator[];
  canManage: boolean;
}

export function SprintEvaluatorsDialog({ open, onOpenChange, sprintId, groupId, evaluators, canManage }: SprintEvaluatorsDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedUserId, setSelectedUserId] = useState('');

  const { data: groupMembers } = useQuery({
    queryKey: ['group-members', groupId],
    queryFn: () => groupService.listMembers(groupId).then((r) => r.data.data),
    enabled: !!groupId && open,
  });

  const evaluatorUserIds = new Set(evaluators.map((e) => e.user.id));
  const availableMembers = (groupMembers ?? []).filter((gm: any) => {
    const uid = gm.user?.id || gm.user_id;
    return !evaluatorUserIds.has(uid);
  });

  const addMut = useMutation({
    mutationFn: (userId: string) => sprintService.addEvaluator(sprintId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprint', sprintId] });
      toast({ title: 'Evaluator added' });
      setSelectedUserId('');
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const removeMut = useMutation({
    mutationFn: (evalId: string) => sprintService.removeEvaluator(sprintId, evalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprint', sprintId] });
      toast({ title: 'Evaluator removed' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Sprint Evaluation Team</DialogTitle>
        </DialogHeader>

        {canManage && (
          <div className="space-y-3">
            <p className="text-sm font-medium">Add Evaluator</p>
            <div className="flex gap-2">
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select member to add" />
                </SelectTrigger>
                <SelectContent>
                  {availableMembers.map((gm: any) => {
                    const uid = gm.user?.id || gm.user_id;
                    const name = gm.user?.full_name || 'Unknown';
                    return <SelectItem key={uid} value={uid}>{name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Button
                className="bg-brand-primary hover:bg-brand-primary-dark"
                disabled={!selectedUserId || addMut.isPending}
                onClick={() => addMut.mutate(selectedUserId)}
              >
                {addMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add</>}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2 mt-2">
          <p className="text-sm font-medium text-muted-foreground">
            Current Evaluators ({evaluators.length})
          </p>
          {evaluators.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No evaluators assigned yet</p>
          ) : (
            <div className="space-y-1">
              {evaluators.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <UserAvatar name={ev.user.full_name} avatarUrl={ev.user.avatar_url} size="sm" />
                    <span className="text-sm font-medium">{ev.user.full_name}</span>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => removeMut.mutate(ev.id)}
                      className="text-muted-foreground hover:text-destructive p-1 rounded transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
